// src/components/property/PropertyStatusBadge.tsx
/**
 * PropertyStatusBadge
 *
 * Displays a color-coded status badge for property listings.
 * Shows combined property + video status.
 */

import { memo } from 'react';
import { Loader2, CheckCircle, AlertCircle, Clock, Video, Sparkles } from 'lucide-react';
import type { IPropertyStatusBadgeProps } from '@/@types/property';

const PropertyStatusBadge = ({
  propertyStatus,
  videoStatus,
  errorMessage,
}: IPropertyStatusBadgeProps) => {
  const getStatusConfig = () => {
    // Failed states (highest priority)
    if (propertyStatus === 'failed' || videoStatus === 'failed') {
      return {
        icon: <AlertCircle className="h-3.5 w-3.5" />,
        label: 'Failed',
        className: 'bg-red-900/30 text-red-400 light:bg-red-100 light:text-red-700',
      };
    }

    // Video completed
    if (videoStatus === 'completed') {
      return {
        icon: <CheckCircle className="h-3.5 w-3.5" />,
        label: 'Completed',
        className: 'bg-green-900/30 text-green-400 light:bg-green-100 light:text-green-700',
      };
    }

    // Video generating
    if (videoStatus && videoStatus !== 'pending') {
      return {
        icon: <Video className="h-3.5 w-3.5 animate-pulse" />,
        label: 'Generating Video',
        className: 'bg-purple-900/30 text-purple-400 light:bg-purple-100 light:text-purple-700',
      };
    }

    // Property ready (no video started)
    if (propertyStatus === 'ready') {
      return {
        icon: <Sparkles className="h-3.5 w-3.5" />,
        label: 'Ready to Generate',
        className: 'bg-yellow-900/30 text-yellow-400 light:bg-yellow-100 light:text-yellow-700',
      };
    }

    // Property processing states
    if (propertyStatus === 'scraping') {
      return {
        icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
        label: 'Scraping...',
        className: 'bg-blue-900/30 text-blue-400 light:bg-blue-100 light:text-blue-700',
      };
    }

    if (propertyStatus === 'analyzing_location') {
      return {
        icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
        label: 'Analyzing...',
        className: 'bg-blue-900/30 text-blue-400 light:bg-blue-100 light:text-blue-700',
      };
    }

    if (propertyStatus === 'generating_description') {
      return {
        icon: <Loader2 className="h-3.5 w-3.5 animate-spin" />,
        label: 'Processing...',
        className: 'bg-blue-900/30 text-blue-400 light:bg-blue-100 light:text-blue-700',
      };
    }

    // Default/unknown
    return {
      icon: <Clock className="h-3.5 w-3.5" />,
      label: 'Pending',
      className: 'bg-gray-800 text-gray-400 light:bg-gray-100 light:text-gray-600',
    };
  };

  const config = getStatusConfig();

  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.className}`}
      title={errorMessage || config.label}
    >
      {config.icon}
      <span>{config.label}</span>
    </div>
  );
};

export const PropertyStatusBadgeMemo = memo(PropertyStatusBadge);
